import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Send, RefreshCw, MessageSquare, AlertTriangle, CheckCircle2,
  HelpCircle, ArrowDownToLine, ArrowUpFromLine, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  note:    { icon: MessageSquare, color: "text-slate-600",  bg: "bg-slate-100",  label: "Note" },
  request: { icon: HelpCircle,    color: "text-amber-700",  bg: "bg-amber-100",  label: "Demande" },
  ack:     { icon: CheckCircle2,  color: "text-emerald-700",bg: "bg-emerald-100",label: "Accusé" },
  alert:   { icon: AlertTriangle, color: "text-red-700",    bg: "bg-red-100",    label: "Alerte" },
};

export default function AgentBridgePage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ subject: "", body: "", kind: "note", sender: "pod-agent" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/agent-bridge/messages");
      setMessages(data.messages || []);
    } catch (e) {
      toast.error("Impossible de charger les messages");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30_000);
    return () => clearInterval(iv);
  }, []);

  const send = async () => {
    if (!form.body.trim()) { toast.error("Body requis"); return; }
    setSending(true);
    try {
      await api.post("/admin/agent-bridge/messages", form);
      toast.success("Message envoyé (et poussé au HUB)");
      setForm({ ...form, subject: "", body: "" });
      load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSending(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="admin-agent-bridge-page">
      <header className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">Cockpit</div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Pont Agent HUB ↔ Pod</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            Canal asynchrone entre l'agent Pod (ici) et l'agent HUB Agence MSC. Les messages sont
            synchronisés automatiquement via l'API <code>agent-bridge</code> (pairing_code).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="agent-bridge-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </header>

      {/* Compose */}
      <Card className="p-5 mb-6">
        <h2 className="font-display font-bold text-base mb-3 flex items-center gap-2">
          <Send className="w-4 h-4 text-[color:var(--brand-sky)]" /> Poster un message vers le HUB
        </h2>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">Expéditeur</Label>
            <select
              value={form.sender}
              onChange={(e) => setForm({ ...form, sender: e.target.value })}
              className="mt-1.5 w-full h-10 rounded-md border border-[hsl(var(--border))] bg-white px-3 text-sm"
              data-testid="agent-bridge-sender"
            >
              <option value="pod-agent">pod-agent (IA)</option>
              <option value="pod-admin">pod-admin (humain)</option>
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">Type</Label>
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="mt-1.5 w-full h-10 rounded-md border border-[hsl(var(--border))] bg-white px-3 text-sm"
              data-testid="agent-bridge-kind"
            >
              <option value="note">Note</option>
              <option value="request">Demande</option>
              <option value="ack">Accusé</option>
              <option value="alert">Alerte</option>
            </select>
          </div>
        </div>
        <div className="mb-3">
          <Label className="text-xs uppercase tracking-wider font-bold">Sujet</Label>
          <Input
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="ex : Status OTA déployé / Demande mise à jour brand colors"
            className="mt-1.5"
            data-testid="agent-bridge-subject"
          />
        </div>
        <div className="mb-4">
          <Label className="text-xs uppercase tracking-wider font-bold">Body (markdown)</Label>
          <Textarea
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            rows={6}
            className="mt-1.5 font-mono text-sm"
            placeholder="Tape ton message en markdown — il sera vu par l'agent HUB lors de sa prochaine session."
            data-testid="agent-bridge-body"
          />
        </div>
        <Button
          onClick={send}
          disabled={sending || !form.body.trim()}
          className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/90"
          data-testid="agent-bridge-send"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
          Envoyer au HUB
        </Button>
      </Card>

      {/* Messages timeline */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-base">
            Messages ({messages.length})
          </h2>
        </div>
        {loading && messages.length === 0 ? (
          <div className="py-10 text-center text-sm text-[hsl(var(--muted-foreground))]">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : messages.length === 0 ? (
          <div className="py-10 text-center text-sm text-[hsl(var(--muted-foreground))]">
            Aucun message — soyez le premier à parler à l'agent HUB.
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((m) => {
              const meta = KIND_META[m.kind] || KIND_META.note;
              const Icon = meta.icon;
              const inbound = (m.sender || "").startsWith("hub-");
              return (
                <div
                  key={m.id}
                  className={`p-4 rounded-xl border ${inbound ? "bg-sky-50/40 border-sky-200" : "bg-white border-[hsl(var(--border))]"}`}
                  data-testid={`agent-bridge-msg-${m.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${meta.bg} ${meta.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${meta.bg} ${meta.color}`}>
                          {meta.label}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--brand-navy)]">
                          {inbound ? <ArrowDownToLine className="w-3 h-3" /> : <ArrowUpFromLine className="w-3 h-3" />}
                          {m.sender}
                        </span>
                        <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
                          {m.created_at ? new Date(m.created_at).toLocaleString("fr-FR") : ""}
                        </span>
                      </div>
                      {m.subject && (
                        <div className="font-display font-semibold text-[color:var(--brand-navy)] mb-1">{m.subject}</div>
                      )}
                      <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{m.body}</div>
                      {m.refs && Object.keys(m.refs).length > 0 && (
                        <details className="mt-2">
                          <summary className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] cursor-pointer">refs</summary>
                          <pre className="mt-1 text-[11px] bg-slate-50 border rounded p-2 overflow-x-auto">{JSON.stringify(m.refs, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
